// Languages active in the resource bundle. English translations exist in JSON
// (kept for future activation) but are excluded from selection / detection.
export const SUPPORTED_LANGS = ['ko', 'ja'] as const;
export type PreviewLang = (typeof SUPPORTED_LANGS)[number];

// Languages that the user can pick from in the UI. Same as SUPPORTED_LANGS for
// now; kept as a separate export so we can reactivate English without
// reshaping the detection / fallback logic.
export const SELECTABLE_LANGS = SUPPORTED_LANGS;

const STORAGE_KEY = 'preview.lang';

function isSupported(lang: string): lang is PreviewLang {
  return (SUPPORTED_LANGS as readonly string[]).includes(lang);
}

function normalize(raw: string | null | undefined): PreviewLang | null {
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (isSupported(lower)) return lower;
  const base = lower.split(/[-_]/)[0];
  if (isSupported(base)) return base;
  return null;
}

export function getStoredLang(): PreviewLang | null {
  if (typeof window === 'undefined') return null;
  try {
    return normalize(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

export function persistLanguage(lang: string): void {
  if (typeof window === 'undefined') return;
  const normalized = normalize(lang);
  if (!normalized) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, normalized);
  } catch {
    /* ignore quota / privacy mode */
  }
}

export function detectLanguage(): PreviewLang {
  const stored = getStoredLang();
  if (stored) return stored;
  if (typeof navigator !== 'undefined') {
    const fromNav = normalize(navigator.language);
    if (fromNav) return fromNav;
    if (Array.isArray(navigator.languages)) {
      for (const candidate of navigator.languages) {
        const matched = normalize(candidate);
        if (matched) return matched;
      }
    }
  }
  return 'ko';
}

export function getPreviewLang(): PreviewLang {
  return getStoredLang() ?? 'ko';
}
